import React from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { doc, getDoc, getDocs, query, where, collection, limit, orderBy, startAfter } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { formatDT } from "@/utils/dates";

export default function StoreOrdersManagement() {
  const [orderId, setOrderId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [error, setError] = React.useState("");
  const [deletePayments, setDeletePayments] = React.useState(true);
  const [result, setResult] = React.useState(null);
  const [newest, setNewest] = React.useState([]);
  const [loadingNewest, setLoadingNewest] = React.useState(true);
  const [busyQuickId, setBusyQuickId] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [pageCursor, setPageCursor] = React.useState(null);
  const [hasMore, setHasMore] = React.useState(true);

  const PAGE_SIZE = 10;

  const fetchPage = React.useCallback(async (reset = false) => {
    setLoadingNewest(true);
    try {
      let qBase = query(collection(db, "orders"));
      // Server-side filter when not 'all'
      if (statusFilter && statusFilter !== "all") {
        qBase = query(qBase, where("status", "==", statusFilter));
      }
      qBase = query(qBase, orderBy("createdAt", "desc"));
      if (!reset && pageCursor) {
        qBase = query(qBase, startAfter(pageCursor));
      }
      qBase = query(qBase, limit(PAGE_SIZE));

      const snap = await getDocs(qBase);
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data(), _doc: d }));
      const append = !reset && newest.length > 0;
      const nextList = append ? [...newest, ...docs] : docs;

      setNewest(nextList.map(({ _doc, ...rest }) => rest));
      const last = snap.docs[snap.docs.length - 1] || null;
      setPageCursor(last);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (e) {
      console.warn("Newest list query failed, attempting fallback:", e?.message || e);
      // Fallback: load unfiltered and client-side filter
      try {
        let qBase = query(collection(db, "orders"), orderBy("createdAt", "desc"));
        if (!reset && pageCursor) qBase = query(qBase, startAfter(pageCursor));
        qBase = query(qBase, limit(PAGE_SIZE));
        const snap = await getDocs(qBase);
        let docs = snap.docs.map((d) => ({ id: d.id, ...d.data(), _doc: d }));
        if (statusFilter && statusFilter !== "all") {
          docs = docs.filter((r) => String(r.status || "").toLowerCase() === statusFilter);
        }
        const append = !reset && newest.length > 0;
        const nextList = append ? [...newest, ...docs] : docs;
        setNewest(nextList.map(({ _doc, ...rest }) => rest));
        const last = snap.docs[snap.docs.length - 1] || null;
        setPageCursor(last);
        setHasMore(snap.docs.length === PAGE_SIZE);
      } catch (e2) {
        console.error("Fallback newest load failed:", e2);
      }
    } finally {
      setLoadingNewest(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, statusFilter, pageCursor, newest]);

  // Initial load
  React.useEffect(() => {
    fetchPage(true);
  }, []); 

  // Reload on status change
  React.useEffect(() => {
    setPageCursor(null);
    setHasMore(true);
    fetchPage(true);
  }, [statusFilter]);

  const fetchPreview = async () => {
    setError("");
    setResult(null);
    setPreview(null);
    const id = orderId.trim();
    if (!id) {
      setError("Enter an order ID");
      return;
    }
    setLoading(true);
    try {
      const orderRef = doc(db, "orders", id);
      const os = await getDoc(orderRef);
      if (!os.exists()) {
        setError("Order not found");
        setLoading(false);
        return;
      }
      const order = { id: os.id, ...os.data() };
      const deliveriesSnap = await getDocs(query(collection(db, "deliveries"), where("orderId", "==", id), limit(50)));
      const pickupsSnap = await getDocs(query(collection(db, "pickups"), where("orderId", "==", id), limit(50)));
      const spSnap = await getDocs(query(collection(db, "storePayments"), where("orderId", "==", id), limit(50)));
      const paySnap = await getDocs(query(collection(db, "payments"), where("linkedId", "==", id), limit(50)));

      setPreview({
        order,
        deliveries: deliveriesSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        pickups: pickupsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        storePayments: spSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        payments: paySnap.docs.map(d => ({ id: d.id, ...d.data() })),
      });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    setError("");
    setResult(null);
    const id = orderId.trim();
    if (!id) {
      setError("Enter an order ID");
      return;
    }

    // Friendly guard: prevent accidental deletion of completed/paid by warning client-side
    const status = String(preview?.order?.status || "").toLowerCase();
    const allowedStatuses = ["canceled", "cancelled", "pending", "trial", "test", "draft"];
    if (preview && status && !allowedStatuses.includes(status)) {
      if (!window.confirm(`Order status is '${status}'. Only canceled/pending/trial are recommended. Continue?`)) {
        return;
      }
    }

    if (!window.confirm("This will permanently delete the order and related records. Type OK to proceed.")) {
      return;
    }

    setLoading(true);
    try {
      const callable = httpsCallable(functions, "adminDeleteOrder");
      const res = await callable({ orderId: id, options: { deletePayments } });
      setResult(res?.data || { ok: true });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const quickPreview = async (id) => {
    setOrderId(id);
    await fetchPreview();
    // Scroll to details for convenience
    try { document.getElementById("som-preview")?.scrollIntoView({ behavior: "smooth" }); } catch (_) {}
  };

  const quickDelete = async (row) => {
    const id = row?.id;
    if (!id) return;
    setError("");
    setResult(null);

    // Client-side guard similar to onDelete
    const status = String(row?.status || "").toLowerCase();
    const allowedStatuses = ["canceled", "cancelled", "pending", "trial", "test", "draft"]; 
    if (status && !allowedStatuses.includes(status)) {
      if (!window.confirm(`Order status is '${status}'. Only canceled/pending/trial are recommended. Continue?`)) return;
    }
    if (!window.confirm("This will permanently delete the order and related records. Type OK to proceed.")) return;

    setBusyQuickId(id);
    try {
      const callable = httpsCallable(functions, "adminDeleteOrder");
      const res = await callable({ orderId: id, options: { deletePayments } });
      setResult(res?.data || { ok: true });
      // Remove from newest list, and clear preview if it matches
      setNewest((prev) => prev.filter((r) => r.id !== id));
      if (preview?.order?.id === id) setPreview(null);
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyQuickId(null);
    }
  };

  return (
    <div className="card p-4 md:p-6 space-y-4">
      <h2 className="text-xl font-semibold">Store Orders Management</h2>
      <p className="text-sm text-ink/70">Search an order ID or pick from the newest list below to review and optionally delete the order, its deliveries, pickups, and store payment records.</p>

      {/* Newest orders quick list */}
      <div className="p-3 border rounded">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="font-medium">Newest Orders (10)</div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-ink/70">Status</label>
            <select
              className="input input-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="pending">pending</option>
              <option value="paid">paid</option>
              <option value="canceled">canceled</option>
              <option value="cancelled">cancelled</option>
              <option value="trial">trial</option>
              <option value="draft">draft</option>
            </select>
            {loadingNewest && <div className="text-xs text-ink/60">Loading…</div>}
          </div>
        </div>
        {!loadingNewest && newest.length === 0 && (
          <div className="text-sm text-ink/60 mt-2">No recent orders.</div>
        )}
        {!loadingNewest && newest.length > 0 && (
          <div className="mt-2 overflow-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-left text-ink/70">
                  <th className="p-2">Date</th>
                  <th className="p-2">Order ID</th>
                  <th className="p-2">Buyer</th>
                  <th className="p-2">Status</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {newest.map((r) => (
                  <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                    <td className="p-2">{formatDT(r.createdAt) || "—"}</td>
                    <td className="p-2 font-mono">{r.id}</td>
                    <td className="p-2">{r.buyerName || r.userId || "—"}</td>
                    <td className="p-2 capitalize">{String(r.status || "pending").toLowerCase()}</td>
                    <td className="p-2 text-right font-mono">₱{Number(r.grandTotal ?? r.subtotal ?? 0).toLocaleString()}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <button className="btn btn-xs btn-outline" onClick={() => quickPreview(r.id)}>Preview</button>
                        <button
                          className="btn btn-xs btn-danger"
                          disabled={busyQuickId === r.id}
                          onClick={() => quickDelete(r)}
                        >
                          {busyQuickId === r.id ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 flex items-center justify-end">
          <button
            className="btn btn-sm btn-outline"
            disabled={loadingNewest || !hasMore}
            onClick={() => fetchPage(false)}
          >
            {hasMore ? (loadingNewest ? "Loading…" : "Load more") : "No more"}
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end">
        <div className="flex-1">
          <label className="block text-sm text-ink/70 mb-1">Order ID</label>
          <input
            className="input w-full"
            placeholder="e.g., ZxY123..."
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
          />
        </div>
        <button className="btn btn-outline" disabled={loading} onClick={fetchPreview}>Preview</button>
      </div>

      {error && (
        <div className="p-3 rounded bg-rose-50 text-rose-700 text-sm">{error}</div>
      )}

      {preview && (
        <div id="som-preview" className="space-y-3">
          <div className="p-3 rounded bg-gray-50 border text-sm">
            <div><span className="font-medium">Order:</span> {preview.order.id}</div>
            <div className="flex flex-wrap gap-4 mt-1">
              <div>Status: <span className="font-medium">{String(preview.order.status || "").toLowerCase() || "—"}</span></div>
              <div>Method: {preview.order.method || "—"}</div>
              <div>Buyer: {preview.order.buyerName || preview.order.userId || "—"}</div>
              <div>Grand Total: ₱{Number(preview.order.grandTotal || 0).toLocaleString()}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 border rounded">
              <div className="font-medium mb-1">Deliveries</div>
              <div className="text-sm">{preview.deliveries.length} found</div>
              {preview.deliveries.length > 0 && (
                <ul className="mt-1 text-xs text-ink/70 space-y-1 max-h-32 overflow-auto">
                  {preview.deliveries.map(d => (<li key={d.id}>{d.id} · {String(d.status || "").toLowerCase()}</li>))}
                </ul>
              )}
            </div>
            <div className="p-3 border rounded">
              <div className="font-medium mb-1">Pickups</div>
              <div className="text-sm">{preview.pickups.length} found</div>
              {preview.pickups.length > 0 && (
                <ul className="mt-1 text-xs text-ink/70 space-y-1 max-h-32 overflow-auto">
                  {preview.pickups.map(d => (<li key={d.id}>{d.id} · {String(d.status || "").toLowerCase()}</li>))}
                </ul>
              )}
            </div>
            <div className="p-3 border rounded">
              <div className="font-medium mb-1">Store Payments</div>
              <div className="text-sm">{preview.storePayments.length} found</div>
              {preview.storePayments.length > 0 && (
                <ul className="mt-1 text-xs text-ink/70 space-y-1 max-h-32 overflow-auto">
                  {preview.storePayments.map(d => (<li key={d.id}>{d.id} · {String(d.status || "").toLowerCase()}</li>))}
                </ul>
              )}
            </div>
            <div className="p-3 border rounded">
              <div className="font-medium mb-1">Payments</div>
              <div className="text-sm">{preview.payments.length} found</div>
              {preview.payments.length > 0 && (
                <ul className="mt-1 text-xs text-ink/70 space-y-1 max-h-32 overflow-auto">
                  {preview.payments.map(d => (<li key={d.id}>{d.id} · {String(d.status || "").toLowerCase()} · {String(d.method || "").toLowerCase()}</li>))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 border rounded">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={deletePayments} onChange={(e) => setDeletePayments(e.target.checked)} />
              Also delete linked Payments (if any)
            </label>
            <button className="btn btn-danger" disabled={loading} onClick={onDelete}>
              Delete Order and Related Records
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="p-3 rounded bg-emerald-50 text-emerald-800 text-sm">
          <div className="font-medium">Deleted successfully</div>
          <pre className="mt-2 text-xs whitespace-pre-wrap">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
